import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useToast } from "@/hooks/use-toast";
import { LogOut } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { Plan } from "@/types";
import { PlanManagement } from "@/components/admin/PlanManagement";
import { WebScraping } from "@/components/admin/WebScraping";
import { UserManagement } from "@/components/admin/UserManagement";
import { ProviderManagement } from "@/components/admin/ProviderManagement";
import { getAuthToken } from "@/utils/authUtils";
import { SeoPageManagement } from "@/components/admin/SeoPageManagement";
import { FaqManagement } from "@/components/admin/FaqManagement";
import { ensureStorageBuckets } from "@/utils/setupStorage";

const Admin = () => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const [plans, setPlans] = useState<Plan[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [currentTab, setCurrentTab] = useState("manage");
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isCheckingAuth, setIsCheckingAuth] = useState(true);

  useEffect(() => {
    checkAuth();
    
    // Check if the cms-images bucket is available
    const checkStorage = async () => {
      await ensureStorageBuckets();
    };
    
    checkStorage();
    
    const { data: authListener } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'SIGNED_IN' && session) {
        setIsAuthenticated(true);
        fetchPlans();
      } else if (event === 'SIGNED_OUT') {
        setIsAuthenticated(false);
        navigate('/login');
      }
    });

    return () => {
      authListener.subscription.unsubscribe();
    };
  }, [navigate]);

  const checkAuth = async () => {
    setIsCheckingAuth(true);
    try {
      const { data: sessionData } = await supabase.auth.getSession();
      if (sessionData.session) {
        setIsAuthenticated(true);
        fetchPlans();
      } else {
        setIsAuthenticated(false);
        toast({
          title: "Authentication required",
          description: "You need to be logged in to access the admin page",
          variant: "destructive",
        });
        navigate('/login');
      }
    } catch (error) {
      console.error('Error checking authentication:', error);
      setIsAuthenticated(false);
    } finally {
      setIsCheckingAuth(false);
    }
  };

  const fetchPlans = async () => {
    if (!isAuthenticated) return;
    
    setIsLoading(true);
    try {
      const token = await getAuthToken();
      
      const response = await fetch('https://rgrwileewnmhlqzdfzui.supabase.co/functions/v1/manage-plans', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          method: 'GET',
          action: 'list'
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to fetch plans');
      }

      const plansData = await response.json();
      setPlans(plansData.plans || []);
    } catch (error) {
      console.error('Error fetching plans:', error);
      toast({
        title: "Error fetching plans",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteAllPlans = async () => {
    if (!confirm("Are you sure you want to delete ALL plans? This action cannot be undone.")) {
      return;
    }

    setIsLoading(true);
    try {
      const token = await getAuthToken();
      
      const response = await fetch('https://rgrwileewnmhlqzdfzui.supabase.co/functions/v1/manage-plans', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          method: 'DELETE',
          action: 'deleteAll'
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to delete all plans');
      }
      
      setPlans([]);
      toast({
        title: "All plans deleted",
        description: "All plans have been deleted successfully",
      });
      
      await fetchPlans();
    } catch (error) {
      console.error('Error deleting all plans:', error);
      toast({
        title: "Error deleting plans",
        description: error.message,
        variant: "destructive",
      });
      fetchPlans();
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogout = async () => {
    await supabase.auth.signOut();
    navigate('/login');
  };

  if (isCheckingAuth) {
    return (
      <div className="container mx-auto px-4 py-8 flex justify-center items-center h-screen">
        <p>Loading admin panel...</p>
      </div>
    );
  }

  if (!isAuthenticated) {
    return null;
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold">Admin Dashboard</h1>
        <Button onClick={handleLogout} variant="outline">
          <LogOut className="h-4 w-4 mr-2" />
          Logout
        </Button>
      </div>

      <Tabs
        defaultValue="manage"
        value={currentTab}
        onValueChange={setCurrentTab}
        className="space-y-6"
      >
        <TabsList className="grid w-full max-w-md grid-cols-6">
          <TabsTrigger value="manage">Plans</TabsTrigger>
          <TabsTrigger value="scrape">Web Scraping</TabsTrigger>
          <TabsTrigger value="providers">Providers</TabsTrigger>
          <TabsTrigger value="users">Users</TabsTrigger>
          <TabsTrigger value="seo">SEO Pages</TabsTrigger>
          <TabsTrigger value="faq">FAQs</TabsTrigger>
        </TabsList>

        <TabsContent value="manage">
          <PlanManagement 
            plans={plans}
            isLoading={isLoading}
            onRefresh={fetchPlans}
            onDeleteAll={handleDeleteAllPlans}
          />
        </TabsContent>

        <TabsContent value="scrape">
          <WebScraping onComplete={fetchPlans} />
        </TabsContent>

        <TabsContent value="providers">
          <ProviderManagement />
        </TabsContent>

        <TabsContent value="users">
          <UserManagement />
        </TabsContent>

        <TabsContent value="seo">
          <SeoPageManagement />
        </TabsContent>

        <TabsContent value="faq">
          <FaqManagement />
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default Admin;
